import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Mic, 
  MicOff, 
  Users, 
  Heart, 
  Scale, 
  HeartHandshake,
  Wifi,
  WifiOff,
  AlertCircle,
  User,
  Bot,
  Volume2,
  VolumeX
} from 'lucide-react';
import { cn } from '@/lib/utils';
import useAudioStream from '@/hooks/useAudioStream';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VoiceIntake = () => {
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  
  const transcriptEndRef = useRef(null);

  const {
    connect,
    disconnect,
    startRecording,
    stopRecording,
    clearTranscript,
    isConnected,
    isRecording,
    transcript,
    sessionId,
    error: audioError
  } = useAudioStream();

  const industryIcons = {
    Medical: Heart,
    Legal: Scale,
    AgedCare: HeartHandshake
  };

  const industryColors = {
    Medical: '#EF4444',
    Legal: '#3B82F6',
    AgedCare: '#10B981'
  };

  const industryBadgeColors = {
    Medical: 'bg-red-500/20 text-red-400 border-red-500/30',
    Legal: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    AgedCare: 'bg-green-500/20 text-green-400 border-green-500/30'
  };

  useEffect(() => {
    fetchTenants();
  }, []);

  useEffect(() => {
    // Auto-scroll to bottom when new transcript messages arrive
    if (transcriptEndRef.current) {
      transcriptEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [transcript]);

  const fetchTenants = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/tenants`);
      setTenants(response.data || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching tenants:', err);
      setError('Failed to load tenants');
    } finally {
      setLoading(false);
    }
  };

  const handleTenantSelect = (tenantId) => {
    const tenant = tenants.find(t => t.tenant_id === tenantId);
    setSelectedTenant(tenant);
    
    // Disconnect previous session if any
    if (isConnected) {
      disconnect();
    }
    
    // Clear previous transcript
    clearTranscript();
  };

  const handleConnect = () => {
    if (!selectedTenant) return;
    
    if (isConnected) {
      disconnect();
    } else {
      connect(selectedTenant.tenant_id);
    }
  };

  const handleRecording = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  const handleClearTranscript = () => {
    clearTranscript();
  };

  const getConnectionStatus = () => {
    if (!selectedTenant) return { text: 'Select Tenant', color: 'text-slate-400' };
    if (!isConnected) return { text: 'Disconnected', color: 'text-red-400' };
    if (isRecording) return { text: 'Recording', color: 'text-green-400' };
    return { text: 'Connected', color: 'text-teal-400' };
  };

  const status = getConnectionStatus();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-400"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header Section */}
      <div className="text-center space-y-4">
        <div className="inline-flex items-center space-x-2 px-4 py-2 rounded-full bg-gradient-to-r from-teal-500/10 to-blue-500/10 border border-teal-500/20">
          <Mic className="w-5 h-5 text-teal-400" />
          <span className="text-white font-medium">Universal Voice Intake</span>
        </div>
        <h1 className="text-4xl font-bold text-white">
          AI-Powered Voice <span className="bg-gradient-to-r from-teal-400 to-blue-500 bg-clip-text text-transparent">Conversations</span>
        </h1>
        <p className="text-xl text-slate-300 max-w-2xl mx-auto">
          Select a tenant and start a voice conversation with our intelligent AI agent
        </p>
      </div>

      {/* Error Alert */}
      {(error || audioError) && (
        <Alert className="bg-red-500/10 border-red-500/20 text-red-400">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {error || audioError}
          </AlertDescription>
        </Alert>
      )}

      {/* Tenant Selection */}
      <Card className="bg-slate-800/50 backdrop-blur border-slate-700">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-white">Select Tenant</h3>
            <div className="flex items-center space-x-2">
              <div className={cn("w-2 h-2 rounded-full", 
                isConnected ? "bg-green-400 animate-pulse" : "bg-slate-500"
              )} />
              <span className={cn("text-sm font-medium", status.color)}>
                {status.text}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Select onValueChange={handleTenantSelect} data-testid="tenant-selector">
              <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                <SelectValue placeholder="Choose a tenant..." />
              </SelectTrigger>
              <SelectContent className="bg-slate-700 border-slate-600">
                {tenants.map((tenant) => {
                  const IndustryIcon = industryIcons[tenant.industry_type] || Users;
                  return (
                    <SelectItem key={tenant.tenant_id} value={tenant.tenant_id} className="text-white hover:bg-slate-600">
                      <div className="flex items-center space-x-2">
                        <IndustryIcon className="w-4 h-4" style={{ color: industryColors[tenant.industry_type] }} />
                        <span>{tenant.name}</span>
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>

            {selectedTenant && (
              <div className="flex items-center space-x-4">
                <Badge 
                  className={cn("px-3 py-1", industryBadgeColors[selectedTenant.industry_type] || 'bg-slate-500/20 text-slate-400')}
                >
                  {selectedTenant.industry_type}
                </Badge>
                <span className="text-sm text-slate-300">
                  Protocol: {selectedTenant.compliance_protocol?.name || 'Standard'}
                </span>
              </div>
            )}
          </div>

          {selectedTenant && (
            <div className="mt-6 flex items-center space-x-4">
              <Button
                onClick={handleConnect}
                disabled={!selectedTenant}
                variant={isConnected ? "destructive" : "default"}
                className={cn(
                  isConnected 
                    ? "bg-red-500 hover:bg-red-600" 
                    : "bg-teal-500 hover:bg-teal-600"
                )}
                data-testid="connect-button"
              >
                {isConnected ? (
                  <>
                    <WifiOff className="w-4 h-4 mr-2" />
                    Disconnect
                  </>
                ) : (
                  <>
                    <Wifi className="w-4 h-4 mr-2" />
                    Connect
                  </>
                )}
              </Button>

              {sessionId && (
                <div className="text-xs text-slate-400">
                  Session: {sessionId.slice(0, 8)}...
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Voice Interface */}
      {isConnected && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Microphone Control */}
          <Card className="bg-slate-800/50 backdrop-blur border-slate-700">
            <CardContent className="p-8 text-center">
              <div className="space-y-6">
                <h3 className="text-xl font-semibold text-white">Voice Control</h3>
                
                {/* Large Microphone Button */}
                <div className="relative">
                  <Button
                    onClick={handleRecording}
                    disabled={!isConnected}
                    size="lg"
                    className={cn(
                      "w-32 h-32 rounded-full text-white transition-all duration-300 transform hover:scale-105",
                      isRecording
                        ? "bg-red-500 hover:bg-red-600 animate-pulse shadow-lg shadow-red-500/50"
                        : "bg-teal-500 hover:bg-teal-600 shadow-lg shadow-teal-500/30"
                    )}
                    data-testid="microphone-button"
                  >
                    {isRecording ? (
                      <MicOff className="w-12 h-12" />
                    ) : (
                      <Mic className="w-12 h-12" />
                    )}
                  </Button>
                  
                  {isRecording && (
                    <div className="absolute inset-0 rounded-full border-4 border-red-400 animate-ping opacity-75"></div>
                  )}
                </div>

                <div className="space-y-2">
                  <p className="text-lg font-medium text-white">
                    {isRecording ? 'Recording...' : 'Click to Start Recording'}
                  </p>
                  <p className="text-sm text-slate-400">
                    {isRecording 
                      ? 'Speak clearly into your microphone' 
                      : 'Press the microphone to begin your conversation'
                    }
                  </p>
                </div>

                {/* Controls */}
                <div className="flex items-center justify-center space-x-4">
                  <Button
                    onClick={() => setIsMuted(!isMuted)}
                    variant="outline"
                    size="sm"
                    className="bg-slate-700 border-slate-600 text-white hover:bg-slate-600"
                    data-testid="mute-button"
                  >
                    {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                  </Button>
                  
                  <Button
                    onClick={handleClearTranscript}
                    variant="outline"
                    size="sm"
                    className="bg-slate-700 border-slate-600 text-white hover:bg-slate-600"
                    data-testid="clear-transcript-button"
                  >
                    Clear Chat
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Conversation Transcript */}
          <Card className="bg-slate-800/50 backdrop-blur border-slate-700">
            <CardContent className="p-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-semibold text-white">Live Conversation</h3>
                  <Badge variant="outline" className="border-teal-500/30 text-teal-400">
                    {transcript.length} messages
                  </Badge>
                </div>

                {/* Transcript Messages */}
                <div className="h-96 overflow-y-auto space-y-4 pr-2" data-testid="transcript-container">
                  {transcript.length === 0 ? (
                    <div className="flex items-center justify-center h-full text-slate-400">
                      <div className="text-center">
                        <Bot className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p>Conversation will appear here...</p>
                        <p className="text-sm">Start recording to begin</p>
                      </div>
                    </div>
                  ) : (
                    transcript.map((message) => (
                      <div
                        key={message.id}
                        className={cn(
                          "flex",
                          message.speaker === 'agent' ? 'justify-start' : 'justify-end'
                        )}
                      >
                        <div
                          className={cn(
                            "max-w-xs lg:max-w-sm px-4 py-3 rounded-lg",
                            message.speaker === 'agent'
                              ? 'bg-slate-700 text-white'
                              : 'bg-teal-500 text-white'
                          )}
                        >
                          <div className="flex items-center space-x-2 mb-1">
                            {message.speaker === 'agent' ? (
                              <Bot className="w-4 h-4" />
                            ) : (
                              <User className="w-4 h-4" />
                            )}
                            <span className="text-xs opacity-75 capitalize">
                              {message.speaker}
                            </span>
                          </div>
                          <p className="text-sm">{message.text}</p>
                        </div>
                      </div>
                    ))
                  )}
                  <div ref={transcriptEndRef} />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Help Section */}
      <Card className="bg-slate-800/30 backdrop-blur border-slate-700/50">
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold text-white mb-4">How to Use Voice Intake</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <div className="w-8 h-8 rounded-full bg-teal-500/20 flex items-center justify-center">
                <span className="text-teal-400 font-bold">1</span>
              </div>
              <p className="text-sm text-white font-medium">Select a Tenant</p>
              <p className="text-xs text-slate-400">Choose the healthcare, legal, or aged care organization</p>
            </div>
            <div className="space-y-2">
              <div className="w-8 h-8 rounded-full bg-teal-500/20 flex items-center justify-center">
                <span className="text-teal-400 font-bold">2</span>
              </div>
              <p className="text-sm text-white font-medium">Connect & Record</p>
              <p className="text-xs text-slate-400">Click connect, then press the microphone to start talking</p>
            </div>
            <div className="space-y-2">
              <div className="w-8 h-8 rounded-full bg-teal-500/20 flex items-center justify-center">
                <span className="text-teal-400 font-bold">3</span>
              </div>
              <p className="text-sm text-white font-medium">Review Conversation</p>
              <p className="text-xs text-slate-400">Watch the real-time transcript and AI responses</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default VoiceIntake;